import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { toast } from 'sonner';

export const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(name, email, password);
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      toast.error(errorMessage);
      setErrors({ form: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SkipLink />
      <Header />
      <main id="main-content" className="flex items-center justify-center min-h-screen px-4 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="p-8 border border-[#E2E8F0] rounded-2xl bg-white shadow-sm">
            <h1 className="text-3xl font-black mb-2 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h1>
            <p className="text-[#475569] text-center mb-8">
              {isLogin ? 'Sign in to your AI interview dashboard' : 'Create your account to start'}
            </p>

            {errors.form && (
              <div 
                role="alert" 
                aria-live="assertive"
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              >
                {errors.form}
              </div>
            )}

            <form 
              onSubmit={handleSubmit} 
              className="space-y-4" 
              data-testid="auth-form"
              noValidate
              aria-label={isLogin ? 'Sign in form' : 'Sign up form'}
            >
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-[#1e293b] font-medium">
                    Full Name <span className="text-red-600" aria-label="required">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                    placeholder="John Doe"
                    aria-required="true"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                      {errors.name}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-[#1e293b] font-medium">
                  Email <span className="text-red-600" aria-label="required">*</span>
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                  placeholder="you@example.com"
                  aria-required="true"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : 'email-hint'}
                  autoComplete="email"
                />
                <p id="email-hint" className="sr-only">
                  Enter your email address
                </p>
                {errors.email && (
                  <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-[#1e293b] font-medium">
                  Password <span className="text-red-600" aria-label="required">*</span>
                </Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                  placeholder="••••••••"
                  minLength={6}
                  aria-required="true"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : 'password-hint'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
                <p id="password-hint" className="mt-1 text-xs text-[#475569]">
                  {!isLogin && 'Must be at least 6 characters'}
                </p>
                {errors.password && (
                  <p id="password-error" className="mt-1 text-sm text-red-600" role="alert">
                    {errors.password}
                  </p>
                )}
              </div>

              <Button
                data-testid="submit-button"
                type="submit"
                className="w-full rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] btn-hover-lift focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                disabled={loading}
                aria-label={loading ? 'Processing' : (isLogin ? 'Sign in to your account' : 'Create your account')}
              >
                {loading ? (
                  <>
                    <span className="sr-only">Processing your request</span>
                    <span aria-hidden="true">Processing...</span>
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E8F0]" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-[#475569]">or</span>
                </div>
              </div>

              <Button
                data-testid="google-auth-btn"
                type="button"
                variant="outline"
                className="w-full rounded-full border-[#E2E8F0] hover:bg-[#F8FAFC]"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
              </Button>

              <button
                data-testid="toggle-auth-mode"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                }}
                className="mt-4 text-sm text-[#1d4ed8] hover:underline focus:outline-none focus:ring-2 focus:ring-[#1d4ed8] focus:ring-offset-2 rounded"
                aria-label={isLogin ? "Switch to sign up form" : "Switch to sign in form"}
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
